/**
 * PostBirthdaySurveyBottomSheet.jsx — v1.0.0 Gate 4 Item 1
 *
 * Non-blocking bottom sheet. Fires on first app open 1-7 days after birthday.
 * Dismissible. Fires once only per birthday year.
 *
 * Questions:
 *   Q1 (required): How was your birthday overall?
 *   Q2: Did you use BirthdayDeaLight on your birthday?
 *   Q3: What did you wish you had more of?
 *   Q4: Any businesses you wished had a birthday offer? (free text, 100 char)
 *        — auto-prefilled with top 2-3 claimed categories as suggestion chips
 *   Q5: What would make next year's birthday better? (free text, 150 char)
 *
 * Awards 25 points. Q4 feeds wanted_promos.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { useUserState } from "@/contexts/UserStateContext";
import {
  trackPostBirthdaySurveyStarted,
  trackPostBirthdaySurveyCompleted,
} from "@/lib/events";

const Q1_OPTIONS = ["Amazing", "Really good", "It was okay", "Disappointing"];
const Q2_OPTIONS = ["Yes absolutely", "Partially", "Not really", "No"];
const Q3_OPTIONS = [
  "More local deals",
  "More food options",
  "Better directions",
  "Earlier reminders",
  "More friends involved",
];

export default function PostBirthdaySurveyBottomSheet() {
  const { user } = useAuth();
  const { state } = useUserState();
  const [eligible, setEligible] = useState(false);
  const [daysAfter, setDaysAfter] = useState(null);
  const [open, setOpen] = useState(false);
  const [startedTracked, setStartedTracked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [chips, setChips] = useState([]);
  const [selectedChips, setSelectedChips] = useState([]);

  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState("");
  const [q4, setQ4] = useState("");
  const [q5, setQ5] = useState("");

  // Eligibility check — only once user + state ready
  useEffect(() => {
    if (!user || state !== "post_birthday_7") return;
    if (localStorage.getItem("pbs_dismissed_this_session") === "1") return;
    let cancelled = false;
    (async () => {
      try {
        const [shouldRes, prefillRes] = await Promise.all([
          axios.get(`${API}/post-birthday-survey/should-show`),
          axios.get(`${API}/post-birthday-survey/prefill`),
        ]);
        if (cancelled) return;
        if (shouldRes.data?.should_show) {
          setEligible(true);
          setDaysAfter(shouldRes.data?.days_after_birthday);
          setChips(prefillRes.data?.chips || []);
          setTimeout(() => setOpen(true), 800);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user, state]);

  // Fire started event on first sheet open
  useEffect(() => {
    if (open && !startedTracked) {
      setStartedTracked(true);
      trackPostBirthdaySurveyStarted(daysAfter);
    }
  }, [open, startedTracked, daysAfter]);

  const toggleChip = (chip) => {
    setSelectedChips((prev) =>
      prev.includes(chip) ? prev.filter((c) => c !== chip) : [...prev, chip]
    );
  };

  const handleDismiss = async () => {
    setOpen(false);
    localStorage.setItem("pbs_dismissed_this_session", "1");
    try {
      await axios.post(`${API}/post-birthday-survey/dismiss`);
    } catch {}
  };

  const handleSubmit = async () => {
    if (!q1) {
      toast.error("Please answer question 1 first");
      return;
    }
    setSubmitting(true);
    try {
      const questionsAnswered = [q1, q2, q3, q4, q5].filter((v) => v && String(v).trim()).length;
      const res = await axios.post(`${API}/post-birthday-survey/submit`, {
        responses: {
          q1_overall: q1,
          q2_used_app: q2,
          q3_more_of: q3,
          q4_wanted_business: q4,
          q4_chips_selected: selectedChips,
          q5_next_year: q5,
        },
      });
      setDone(true);
      trackPostBirthdaySurveyCompleted(daysAfter, questionsAnswered);
      if (res.data?.points_awarded) {
        toast.success(`+${res.data.points_awarded} points!`);
      }
      setTimeout(() => setOpen(false), 2500);
    } catch {
      toast.error("Couldn't submit — try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (!eligible) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 z-[90]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleDismiss}
            data-testid="pbs-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="fixed bottom-0 left-0 right-0 z-[100] max-w-md mx-auto bg-white rounded-t-3xl shadow-2xl max-h-[88vh] overflow-y-auto"
            data-testid="post-birthday-survey-sheet"
          >
            <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-100 flex items-center justify-between px-5 py-3 rounded-t-3xl">
              <div>
                <h2 className="text-sm font-bold text-slate-800" data-testid="pbs-title">
                  Quick birthday check-in
                </h2>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  +25 points. Takes under a minute.
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700"
                data-testid="pbs-dismiss-btn"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {done ? (
              <div className="px-5 py-10 text-center" data-testid="pbs-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-base font-bold text-slate-800">Thanks — we hear you.</p>
                <p className="text-xs text-slate-500 mt-1">Your answers shape what comes next.</p>
              </div>
            ) : (
              <div className="px-5 py-4 space-y-6">
                {/* Q1 */}
                <Section
                  label="How was your birthday overall?"
                  required
                  testId="pbs-q1"
                >
                  <Chips options={Q1_OPTIONS} value={q1} onChange={setQ1} testIdPrefix="pbs-q1" />
                </Section>

                {/* Q2 */}
                <Section
                  label="Did you use BirthdayDeaLight on your birthday?"
                  testId="pbs-q2"
                >
                  <Chips options={Q2_OPTIONS} value={q2} onChange={setQ2} testIdPrefix="pbs-q2" />
                </Section>

                {/* Q3 */}
                <Section
                  label="What did you wish you had more of?"
                  testId="pbs-q3"
                >
                  <Chips options={Q3_OPTIONS} value={q3} onChange={setQ3} testIdPrefix="pbs-q3" />
                </Section>

                {/* Q4 */}
                <Section
                  label="Any businesses you wished had a birthday offer?"
                  testId="pbs-q4"
                >
                  {chips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="pbs-q4-chips">
                      {chips.map((chip) => {
                        const active = selectedChips.includes(chip);
                        return (
                          <button
                            key={chip}
                            type="button"
                            onClick={() => toggleChip(chip)}
                            className={`px-2.5 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${
                              active
                                ? "bg-sky-500 text-white border-sky-500"
                                : "bg-sky-50 text-sky-600 border-sky-200"
                            }`}
                            data-testid={`pbs-q4-chip-${chip.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {chip}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <textarea
                    value={q4}
                    onChange={(e) => setQ4(e.target.value.slice(0, 100))}
                    placeholder="e.g., Chipotle, local bakery down the street…"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-sky-200"
                    rows={2}
                    maxLength={100}
                    data-testid="pbs-q4-textarea"
                  />
                  <p className="text-[10px] text-slate-400 mt-1 text-right">{q4.length}/100</p>
                </Section>

                {/* Q5 */}
                <Section
                  label="What would make next year's birthday better?"
                  testId="pbs-q5"
                >
                  <textarea
                    value={q5}
                    onChange={(e) => setQ5(e.target.value.slice(0, 150))}
                    placeholder="Any ideas, big or small…"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-sky-200"
                    rows={2}
                    maxLength={150}
                    data-testid="pbs-q5-textarea"
                  />
                  <p className="text-[10px] text-slate-400 mt-1 text-right">{q5.length}/150</p>
                </Section>

                <button
                  onClick={handleSubmit}
                  disabled={!q1 || submitting}
                  className="w-full py-3.5 rounded-2xl bg-sky-500 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="pbs-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Section({ label, required, children, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold text-slate-700 mb-2">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </p>
      {children}
    </div>
  );
}

function Chips({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${
              active
                ? "bg-slate-800 text-white border-slate-800"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`${testIdPrefix}-opt-${opt.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}
